import { MEMBERSHIP_ROUTE, QUOTE_ROUTE } from 'constants/routes';
import { useState } from 'react';
import { decline, softDecline } from 'slices/decline';
import { addCompletedCover } from 'slices/selectCover';
import { Cover } from 'types/cover';
import { MulesoftError } from 'types/error';
import { PolicyResponse } from 'types/policy';
import { AppRoute } from 'types/types';
import { coverToRoute, isFetchBaseQueryErrorType, isNotEmptyObjOrArray } from 'utils';

import ERRORS from '~/constants/errors';
import { selectIsReferralVelocity } from '~/selectors/selectAppDetails';
import { AppDispatch } from '~/store';
import { navigateAction } from '~/thunks/navigateAction';
import { quotePremiumsAction } from '~/thunks/quotePremiumsAction';
import { logError } from '~/utils/logError';
import { hasProperty } from '~/utils/types';

import { useAppContent } from './useAppContent';
import { Navigate, useAppNavigate } from './useAppNavigate';
import { useAppDispatch, useAppSelector, useSelectCover } from './useSelectors';

interface CoverDetails {
  cover: Cover;
  onSuccess?: (data: PolicyResponse) => void;
  onError?: (error: unknown) => void;
}

const lastCoverMatch = (cover: Cover, covers: Cover[]) => covers[covers.length - 1] === cover;

/**
 * Returns an effect that returns actionText based on whether
 * the currently viewed cover is the last
 * or other cover details need to be collected
 */
const getActionTextEffect = (isLastCover: boolean, hasMembership = false) => ({
  useActionText: () => {
    const { getTextContent } = useAppContent({ basePath: 'dsme.coverDetails.buttons' });
    return {
      actionText:
        isLastCover && !hasMembership ? getTextContent('lastCover') : getTextContent('nextCover'),
    };
  },
});

const handleError = (error: unknown, dispatch: AppDispatch) => {
  logError(error);
  if (!isNotEmptyObjOrArray(error)) return;

  if (isFetchBaseQueryErrorType(error)) {
    const errData = error.data as MulesoftError;

    if (hasProperty(errData, 'errors')) {
      dispatch(decline({ message: JSON.stringify(errData.errors) }));
    } else if (hasProperty(errData, 'error')) {
      dispatch(decline({ message: errData.error }));
    } else {
      dispatch(decline(ERRORS.malformedMulesoftError));
    }
  } else {
    dispatch(decline(ERRORS.genericDecline));
  }
};

const getSoftDeclineDetails = ({ cover, covers }: { cover: Cover; covers: Cover[] }) => {
  const isBACCover = cover === Cover.BuildingAndContents;
  const isBACAndBICover = isBACCover && covers.includes(Cover.BusinessInterruption);

  const isLastCover = lastCoverMatch(cover, covers);
  const isLastSoftDeclineCover = isBACAndBICover
    ? lastCoverMatch(Cover.BusinessInterruption, covers)
    : isLastCover;

  const { useActionText: useButtonSoftDeclineText } = isBACAndBICover
    ? getActionTextEffect(lastCoverMatch(Cover.BusinessInterruption, covers))
    : getActionTextEffect(isLastCover);
  const nextRoute = getNextRoute(cover, covers);
  const nextSoftDeclineRoute = isBACAndBICover
    ? getNextRoute(Cover.BusinessInterruption, covers)
    : nextRoute;
  const coversToRemove = isBACCover ? [cover, Cover.BusinessInterruption] : [cover];
  const coversToDisplay = isBACAndBICover ? [cover, Cover.BusinessInterruption] : [cover];
  return {
    useActionText: useButtonSoftDeclineText,
    nextRoute: nextSoftDeclineRoute,
    isLastCover: isLastSoftDeclineCover,
    coversToRemove,
    coversToDisplay,
  };
};

const handleMutationResult = ({
  data,
  cover,
  onSuccess,
  onError,
  covers,
  navigate,
  dispatch,
  hasMembership = false,
}: {
  onSuccess: CoverDetails['onSuccess'];
  onError: CoverDetails['onError'];
  data: PolicyResponse;
  cover: Cover;
  covers: Cover[];
  navigate: Navigate;
  dispatch: AppDispatch;
  hasMembership?: boolean;
}) => {
  const isSoftDecline = cover !== Cover.Liability;

  const softDeclineDetails = getSoftDeclineDetails({
    cover,
    covers,
  });
  const { policyTransactionInformation } = data.policy.policyHeader;
  const isReject = !!policyTransactionInformation?.policyMessageStatus;

  if (isReject) {
    const errors = policyTransactionInformation?.extendedStatus;

    onError?.(errors);
    if (!errors) {
      if (isSoftDecline) dispatch(softDecline({ ...softDeclineDetails, ...ERRORS.genericDecline }));
      else dispatch(decline(ERRORS.genericDecline));
    } else {
      const declineDetails = { message: JSON.stringify(errors) };

      if (isSoftDecline) dispatch(softDecline({ ...softDeclineDetails, ...declineDetails }));
      else dispatch(decline(declineDetails));
    }
  } else {
    dispatch(addCompletedCover(cover));
    onSuccess?.(data);
    dispatch(
      navigateAction({
        to: getNextRoute(cover, covers, hasMembership),
        navigate,
      })
    );
  }
};

/**
 * @note we don't have to worry aobut the last cover causing an index out of bounds error
 * as the below util will have the smarts to only call it when it needs the next route
 * and if it is the last route, it will just use the quote route
 */
export const getNextRoute = (
  currentCover: Cover,
  covers: Cover[],
  hasMembership: boolean = false
): AppRoute => {
  const isLastCover = lastCoverMatch(currentCover, covers);
  if (isLastCover) {
    return hasMembership ? MEMBERSHIP_ROUTE : QUOTE_ROUTE;
  }

  const nextCover = covers[covers.indexOf(currentCover) + 1];

  if (!nextCover) throw new Error('Unable to retrieve a next cover');

  return coverToRoute[nextCover];
};

export const useCoverDetailsEffects = (coverDetails: CoverDetails) => {
  const { cover, onSuccess, onError } = coverDetails;
  const { covers } = useSelectCover();
  const hasMembership = useAppSelector(selectIsReferralVelocity);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const quotePremiums = async () => {
    try {
      setIsLoading(true);
      const data = await dispatch(quotePremiumsAction()).unwrap();
      if (data) {
        handleMutationResult({
          data,
          covers,
          cover,
          onSuccess,
          onError,
          navigate,
          dispatch,
          hasMembership,
        });
      }
    } catch (e) {
      handleError(e, dispatch);
    } finally {
      setIsLoading(false);
    }
  };

  const isLastCover = lastCoverMatch(cover, covers);
  const buttonText = getActionTextEffect(isLastCover, hasMembership).useActionText().actionText;

  return {
    quotePremiums,
    isLoading,
    buttonText,
  };
};
